/**
 * Utility functions for generating consistent gradient colors for avatars
 * based on user/team member IDs.
 */

// Full Tailwind class strings so the compiler includes them
const GRADIENT_CLASSES = [
  "bg-gradient-to-br from-blue-500 to-purple-600",
  "bg-gradient-to-br from-green-500 to-blue-600",
  "bg-gradient-to-br from-pink-500 to-red-600",
  "bg-gradient-to-br from-yellow-500 to-orange-600",
  "bg-gradient-to-br from-purple-500 to-pink-600",
  "bg-gradient-to-br from-indigo-500 to-purple-600",
  "bg-gradient-to-br from-teal-500 to-green-600",
  "bg-gradient-to-br from-cyan-500 to-blue-600",
  "bg-gradient-to-br from-emerald-500 to-teal-600",
  "bg-gradient-to-br from-violet-500 to-purple-600",
  "bg-gradient-to-br from-amber-500 to-yellow-600",
  "bg-gradient-to-br from-rose-500 to-pink-600",
  "bg-gradient-to-br from-sky-500 to-cyan-600",
  "bg-gradient-to-br from-lime-500 to-green-600",
  "bg-gradient-to-br from-orange-500 to-red-600",
  "bg-gradient-to-br from-fuchsia-500 to-purple-600",
  "bg-gradient-to-br from-slate-500 to-gray-600",
  "bg-gradient-to-br from-stone-500 to-neutral-600",
  "bg-gradient-to-br from-zinc-500 to-gray-600",
  "bg-gradient-to-br from-red-500 to-rose-600",
] as const

/**
 * Simple hash function to convert string to number.
 */
function hashString(str: string): number {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    hash = (hash << 5) - hash + char
    hash = hash & hash
  }
  return hash
}

/**
 * Returns a consistent gradient class based on a string ID.
 * @param id - Unique identifier (account id, member id, email, etc.)
 * @returns Tailwind gradient class string (e.g. "from-blue-500 to-purple-600")
 */
export function getAvatarGradient(id: string): string {
  if (!id) {
    return "bg-gradient-to-br from-gray-400 to-gray-500"
  }
  const index = Math.abs(hashString(id)) % GRADIENT_CLASSES.length
  return GRADIENT_CLASSES[index]
}

/**
 * Returns a consistent gradient class for a team/member avatar.
 * @param memberId - The member's unique ID (e.g. account_id)
 * @returns Tailwind gradient class string
 */
export function getMemberAvatarGradient(memberId: string): string {
  return getAvatarGradient(memberId)
}
